import {
  clearSoftphoneState,
  readSoftphoneState,
  writeSoftphoneState,
} from './softphoneStorage'
import {
  CCP_SOFTPHONE_ROUTE_PATH,
  CCP_SOFTPHONE_SESSION_EXPIRED_STORAGE_KEY,
} from './types'

/**
 * Whether the current window is the dedicated softphone popup — used to
 * special-case auth-failure handling so this window never redirects itself
 * away from a live call. See CT-821.
 */
export function isCcpSoftphoneWindow(): boolean {
  return window.location.pathname === CCP_SOFTPHONE_ROUTE_PATH
}

/**
 * Records that the softphone popup's session has expired (thymebox's own
 * Auth0 session, or the Amazon Connect Streams session), so main tabs can
 * prompt the agent to reopen a fresh, re-authenticated popup.
 *
 * Persisted rather than broadcast so that main tabs opened after the popup
 * died still see it. See CT-821.
 */
export function notifySoftphoneSessionExpired(): void {
  writeSoftphoneState(
    CCP_SOFTPHONE_SESSION_EXPIRED_STORAGE_KEY,
    new Date().toISOString()
  )
}

/**
 * Clears the expired marker. Only the softphone popup should call this, and
 * only once it has proven it holds a working session — otherwise a reopened
 * but still-unauthenticated popup would silence a warning that is still true.
 */
export function clearSoftphoneSessionExpired(): void {
  clearSoftphoneState(CCP_SOFTPHONE_SESSION_EXPIRED_STORAGE_KEY)
}

/**
 * Whether the softphone popup has reported an expired session that has not
 * since been cleared by a healthy popup.
 */
export function isSoftphoneSessionExpired(): boolean {
  return !!readSoftphoneState(CCP_SOFTPHONE_SESSION_EXPIRED_STORAGE_KEY)
}

/**
 * Subscribes to changes in the expired state made by *other* windows. Returns
 * a cleanup function that unsubscribes.
 *
 * `storage` events only fire in windows other than the writer, which is
 * exactly the direction needed here: the popup writes, the main tabs listen.
 * @param onChange called with the new expired state
 */
export function onSoftphoneSessionExpiryChange(
  onChange: (expired: boolean) => void
): () => void {
  /** @param event */
  function handleStorage(event: StorageEvent) {
    // A null key means the whole store was cleared, which can change our
    // value too, so re-read in that case rather than ignoring it.
    if (
      event.key !== null &&
      event.key !== CCP_SOFTPHONE_SESSION_EXPIRED_STORAGE_KEY
    ) {
      return
    }
    onChange(isSoftphoneSessionExpired())
  }

  window.addEventListener('storage', handleStorage)
  return () => window.removeEventListener('storage', handleStorage)
}
